import React, {useEffect} from 'react';
import {Provider} from 'mobx-react';
import {LoaderFunctionArgs, redirect, useLoaderData, useNavigation, useRouteError} from 'react-router-dom';
import {getRootStore, addErrorEventLog} from '../app.bootstrap';
import {getAppTracer} from '../../app.tracer';
import {AuthHandlerResult} from '../services/auth/auth-handler.service';
import {RouteName} from './route-names';
import {sendSentryLog} from '../../error-reporting';
import {LOG_EVENTS} from '@techsee/techsee-common/lib/constants/event-logs.constants';

const trace = getAppTracer('routing.infra');

/**
 * Base interface for route stores containing services, controllers, and state.
 * All route stores must implement dispose() for cleanup and can access navigate() for routing.
 */
export interface IRouteStore {
    dispose(): void;

    [key: string]: any;
}

/**
 * Async function that loads and returns route stores before component renders
 */
export type RouteLoader<STORE extends IRouteStore = IRouteStore> = (args?: LoaderFunctionArgs) => Promise<STORE | void>;

/**
 * HOC that wraps a simple React component with MobX Provider to inject stores from loader data.
 * Used for simplified route modules that don't use RouteComponent classes.
 * Automatically handles loading states, store disposal, and Provider wrapping.
 */
export function withStoresProvider<S extends IRouteStore>(
    Component: React.ComponentType<any>,
    storeAs: string
): React.ComponentType<any> {
    function WrappedWithProvider(props: any): JSX.Element {
        const navigation = useNavigation();

        trace.info('withStoresProvider render', {
            storeAs,
            navigationState: navigation.state
        });

        // Get loader data
        let stores: S;
        try {
            stores = useLoaderData() as S;
            trace.info('withStoresProvider: useLoaderData succeeded', {storeAs});
        } catch (error) {
            trace.error('withStoresProvider: Failed to get loader data, using empty object', {error});
            sendSentryLog(error);
            stores = {} as any;
        }

        // Cleanup on unmount
        useEffect(() => {
            return () => {
                trace.info(`withStoresProvider: Store "${storeAs}" destroyed`);
                if (stores?.dispose) {
                    setTimeout(() => stores.dispose());
                }
            };
        }, [stores]);

        // Wrap with Provider using the storeAs name
        const providerProps = {
            [storeAs]: stores
        };

        trace.info('withStoresProvider: Rendering with Provider', {storeAs});

        return (
            <Provider {...providerProps}>
                <Component {...props} />
            </Provider>
        );
    }

    WrappedWithProvider.displayName = `withStoresProvider(${Component.displayName || (Component as any).name || 'Component'})`;
    return WrappedWithProvider;
}

/**
 * HOC that wraps lazy-loaded page components and hides global loader after render.
 * Applied to components loaded via React Router's lazy() function.
 */
export function withLazyPageWrapper<T extends React.ComponentType<any>>(PageComponent: T): T {
    const Wrapped = (props: React.ComponentProps<T>) => {
        const rootStore = getRootStore();

        useEffect(() => {
            rootStore.appState.showGlobalLoader = false;
        }, []);

        return <PageComponent {...props} />;
    };

    Wrapped.displayName = `LazyPage(${PageComponent.displayName ?? (PageComponent as any).name ?? 'Anonymous'})`;

    return Wrapped as T;
}

/**
 * Development mode flag - React StrictMode intentionally double-invokes loaders in development
 */
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Cache for loader promises to prevent duplicate executions during React StrictMode double-invocations
 */
const loaderCache = new Map<string, {promise: Promise<any>; timestamp: number}>();
const CACHE_DURATION = 50; // ms - short cache to prevent StrictMode double calls

/**
 * Map to track parent loader promises for sequential execution
 */
const parentLoaderPromises = new Map<string, Promise<any>>();

/**
 * Map to track resolvers for placeholder promises
 */
const placeholderResolvers = new Map<string, (value: any) => void>();

/**
 * Registers a placeholder promise for a lazy route's loader.
 * This allows child routes to wait for lazy-loaded parent loaders.
 * The placeholder is resolved when the actual loader completes.
 * Redirect Responses are resolved (not rejected) — see wrapLoaderWithContext.
 */
export function registerLazyLoaderPlaceholder(routePath: string): void {
    if (!parentLoaderPromises.has(routePath)) {
        let resolver: (value: any) => void;
        const placeholder = new Promise<any>((resolve) => {
            resolver = resolve;
        });

        parentLoaderPromises.set(routePath, placeholder);
        placeholderResolvers.set(routePath, resolver!);
        trace.info('registerLazyLoaderPlaceholder: Registered placeholder', {routePath});
    }
}

/**
 * Wraps loader to ensure sequential execution - parent completes before child starts.
 * Supports unlimited nesting levels through recursive parent path tracking.
 * Redirect Responses propagated from parent loaders are detected and returned early
 * so that child loaders are never executed after a parent redirect.
 */
export function wrapLoaderSequential(loader: any, routePath: string, parentPath?: string): any {
    if (!loader) return loader;

    return async (...args: any[]) => {
        // Wait for parent loader to complete if it exists
        if (parentPath && parentLoaderPromises.has(parentPath)) {
            trace.info('wrapLoaderSequential: Waiting for parent', {parentPath, currentPath: routePath});
            const parentResult = await parentLoaderPromises.get(parentPath);
            trace.info('wrapLoaderSequential: Parent completed', {parentPath, currentPath: routePath});

            // If parent returned a redirect Response, propagate it — don't run child loader
            if (parentResult instanceof Response) {
                trace.info('wrapLoaderSequential: Parent redirected, skipping child loader', {routePath});
                return parentResult;
            }
        }

        trace.info('wrapLoaderSequential: Sequential Loader', {routePath});
        const promise = loader(...args);

        // If there was a placeholder, resolve it when the actual loader settles
        if (placeholderResolvers.has(routePath)) {
            const resolver = placeholderResolvers.get(routePath)!;
            placeholderResolvers.delete(routePath);
            promise.then(
                (result: any) => {
                    resolver(result);
                    trace.info('wrapLoaderSequential: Resolved placeholder', {routePath});
                },
                () => {
                    // Should not happen — wrapLoaderWithContext converts thrown Responses to returns.
                    // Resolve with undefined as a safety net so children don't hang.
                    resolver(undefined);
                    trace.info('wrapLoaderSequential: Resolved placeholder after unexpected rejection', {routePath});
                }
            );
            trace.info('wrapLoaderSequential: Using existing placeholder', {routePath});
        } else {
            // No placeholder, store the promise normally
            parentLoaderPromises.set(routePath, promise);
        }

        // Clean up after completion
        promise.finally(() => {
            setTimeout(() => {
                parentLoaderPromises.delete(routePath);
                placeholderResolvers.delete(routePath);
                trace.info('wrapLoaderSequential: Cleaned up loader', {routePath});
            }, 1000); // Keep for 1 second in case children need it
        });

        return promise;
    };
}

/**
 * Wraps route loaders to prevent duplicate executions in development mode.
 * Returns cached promise if loader called multiple times within 100ms (StrictMode scenario).
 * In production, returns unwrapped loader with no overhead.
 */
export function wrapLoaderWithCache(loader: any, routePath: string): any {
    if (!loader) return loader;

    // In production, return unwrapped loader
    if (!isDevelopment) {
        return loader;
    }

    // In development, wrap with cache to prevent StrictMode double-invocations
    return async (...args: any[]) => {
        const cacheKey = routePath;
        const now = Date.now();
        const cached = loaderCache.get(cacheKey);

        // If there's a cached promise from very recently (StrictMode double-invoke), return it
        if (cached && now - cached.timestamp < CACHE_DURATION) {
            trace.info('wrapLoaderWithCache: Cache hit', {cacheKey});
            return cached.promise;
        }

        // Create new promise and cache it
        trace.info('wrapLoaderWithCache: Cached Loader (not hit)', {cacheKey});
        const promise = loader(...args);
        loaderCache.set(cacheKey, {promise, timestamp: now});

        // Clean up cache after completion
        promise.then(
            () => {
                setTimeout(() => {
                    const entry = loaderCache.get(cacheKey);
                    if (entry && entry.timestamp === now) {
                        loaderCache.delete(cacheKey);
                    }
                }, CACHE_DURATION);
            },
            () => {
                // On error, remove immediately
                loaderCache.delete(cacheKey);
            }
        );

        return promise;
    };
}

/**
 * Cache for parent route loader results, keyed by route path.
 * Cleared when the route store's dispose() is called.
 */
const parentLoaderCache = new Map<string, Promise<IRouteStore>>();

/**
 * Wraps a parent route loader (any route that has children) so its result is cached for
 * the lifetime of the route. If the loader is called a second time while the first result
 * is still live (e.g. React Router re-runs the parent after a child-loader redirect during
 * the initial page load), the cached Promise is returned immediately — no re-run.
 *
 * Cache invalidation is wired into the route store's own dispose() so the next cold
 * navigation always gets a fresh run.
 */
export function wrapLoaderForParentRoute(loader: any, routePath: string): any {
    if (!loader) return loader;

    return async (...args: any[]) => {
        if (parentLoaderCache.has(routePath)) {
            return parentLoaderCache.get(routePath);
        }

        const promise = loader(...args).then((result: IRouteStore) => {
            if (result instanceof Response) {
                parentLoaderCache.delete(routePath);
                return result;
            }

            const originalDispose = result.dispose.bind(result);

            result.dispose = () => {
                parentLoaderCache.delete(routePath);
                originalDispose();
            };

            return result;
        });

        parentLoaderCache.set(routePath, promise);

        return promise;
    };
}

/**
 * Wraps ALL loaders with loader context enter/exit.
 * This ensures navigateTo() throws redirect() inside any loader, regardless of authMethod.
 * Redirect Responses are caught and RETURNED instead of thrown — React Router handles both
 * identically, but returning avoids Bluebird flagging the rejection as unhandled
 * (Bluebird replaces the global Promise via expose-loader in webpack.config.js).
 */
export function wrapLoaderWithContext(loader: any): any {
    if (!loader) return loader;

    return async (...args: any[]) => {
        const rootStore = getRootStore();

        rootStore.tsReactStateHelper.enterLoaderContext();
        try {
            return await loader(...args);
        } catch (error) {
            if (error instanceof Response) {
                return error;
            }

            console.error('##### Loader error:', error);

            addErrorEventLog(LOG_EVENTS.routeLoaderError.type, {
                error: error instanceof Error ? error.message : String(error),
                stack: error instanceof Error ? error.stack : undefined
            });

            sendSentryLog(error);
            return redirect('/login');
        } finally {
            rootStore.tsReactStateHelper.exitLoaderContext();
        }
    };
}

/**
 * Wraps a loader with authentication based on authMethod configuration.
 * If authMethod is false, returns the loader unchanged (no auth check).
 * Auth redirects use navigateTo() which throws in loader context.
 */
export function wrapLoaderWithAuth(
    loader: any,
    authMethod: boolean | ((args: LoaderFunctionArgs) => Promise<AuthHandlerResult>) | undefined,
    ignoreReturnUrl?: boolean
): any {
    if (authMethod === false) {
        return loader;
    }

    return async (args: LoaderFunctionArgs) => {
        const rootStore = getRootStore();
        let authResult: AuthHandlerResult;

        try {
            if (typeof authMethod === 'function') {
                authResult = await authMethod(args);
            } else {
                const {protectedRouteLoader} = await import('../services/auth/auth-handler.service');

                authResult = await protectedRouteLoader(args, {ignoreReturnUrl});
            }
        } catch (authError) {
            addErrorEventLog(LOG_EVENTS.routeAuthError.type, {
                error: authError instanceof Error ? authError.message : String(authError),
                url: args.request.url
            });

            sendSentryLog(authError);
            throw authError;
        }

        if (!authResult.authorized) {
            await rootStore.tsReactStateHelper.navigateTo(authResult.state || RouteName.LOGIN, authResult.params);
        }

        return await loader(args);
    };
}

/**
 * Authentication method for route protection
 * - undefined or true: Default protected route (requires auth token)
 * - false: No authentication required
 * - function: Custom authentication logic
 */
export type AuthMethod = boolean | ((args: LoaderFunctionArgs) => Promise<Response | Record<string, any>>);

/**
 * Extended RouteObject with authentication configuration
 */
export type TechseeRoute = any;
// export interface TechseeRoute extends Omit<ReactRouterObject, 'children'> {
//     authMethod?: AuthMethod;
//     children?: TechseeRoute[];
//     loader?: RouteLoader<any>;
//     lazy: any;
//     element: React.ReactElement;
//     path:string
// }

/**
 * Standard interface for routable module exports.
 * Each route module should export loader, Component, storeAs, and optional routing configuration.
 * Component should be a React functional or class component (not RouteComponent).
 * Stores are automatically injected via Provider using the storeAs property name.
 */
export interface IRoutableModule {
    loader?: RouteLoader<any>;
    Component: React.ComponentType<any>;
    MobileComponent?: React.ComponentType<any>; // Optional mobile-specific component
    storeAs?: string; // Property name for store injection (e.g., 'dashboardStore')
    errorElement?: React.ComponentType<any>;
}

/**
 * HOC that renders different components based on device type.
 * If MobileComponent is not provided, DesktopComponent is used for all devices.
 */
export function withResponsiveComponent(
    DesktopComponent: React.ComponentType<any>,
    MobileComponent?: React.ComponentType<any>
): React.ComponentType<any> {
    if (!MobileComponent) {
        return DesktopComponent;
    }

    const MobileComp = MobileComponent;

    function ResponsiveWrapper(props: any): JSX.Element {
        const rootStore = getRootStore();
        const displayTabletAsDesktop = rootStore.displayTabletAsDesktop;
        const isMobile = rootStore.environmentService.isMobile(displayTabletAsDesktop);

        const Component = isMobile ? MobileComp : DesktopComponent;
        return React.createElement(Component, props);
    }

    ResponsiveWrapper.displayName = `Responsive(${DesktopComponent.displayName || (DesktopComponent as any).name || 'Component'})`;

    return ResponsiveWrapper;
}

/**
 * Root-level error boundary that renders the actual error for debugging.
 */
export function RouteErrorBoundary(): JSX.Element {
    const error = useRouteError();

    console.error('##### Route error boundary:', error);

    const errorObj = {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        url: window.location.href
    };

    addErrorEventLog(LOG_EVENTS.routeRenderError.type, errorObj);
    sendSentryLog(errorObj);

    const message = error instanceof Error ? error.message : String(error);
    const stack = error instanceof Error ? error.stack : undefined;

    return (
        <div style={{padding: 24, fontFamily: 'monospace'}}>
            <h2>Something went wrong</h2>
            <pre style={{whiteSpace: 'pre-wrap', color: '#c00'}}>{message}</pre>
            {stack && <pre style={{whiteSpace: 'pre-wrap', color: '#666', fontSize: 12}}>{stack}</pre>}
        </div>
    );
}
